import { SubgraphType } from 'config/subgraph'
import { useSubgraphConfig } from 'hooks/useSubgraphConfig'
import { useMemo } from 'react'
import { Transaction, TransactionType } from 'types/analytics'

import type { GetV2TokenTransactionsQuery } from '../../queries/analytics/v2/generated'
import { useGetV2TokenPairsQuery, useGetV2TokenTransactionsQuery } from '../../queries/analytics/v2/generated'

type TokenTransactionsResponse = GetV2TokenTransactionsQuery

type MintTx = NonNullable<TokenTransactionsResponse['mintV2S']>[number]
type BurnTx = NonNullable<TokenTransactionsResponse['burnV2S']>[number]
type SwapTx = NonNullable<TokenTransactionsResponse['swapV2S']>[number]

const parseAmount = (value?: string | null) => {
  if (!value) return 0
  const parsed = Number.parseFloat(value)
  return Number.isFinite(parsed) ? parsed : 0
}

const getTimestamp = (tx: { transaction?: { timestamp?: string } }): string => {
  const timestamp = tx.transaction?.timestamp
  return timestamp ? timestamp : '0'
}

const toProcessedTransaction = (
  tx: MintTx | BurnTx | SwapTx,
  type: TransactionType,
  sender: string,
  token0Amount: number,
  token1Amount: number,
  amountUSD: number
): Transaction => ({
  type,
  hash: tx.transaction.id,
  timestamp: getTimestamp(tx),
  sender,
  token0: {
    symbol: tx.pair.token0.symbol,
    address: tx.pair.token0.id,
    amount: token0Amount,
  },
  token1: {
    symbol: tx.pair.token1.symbol,
    address: tx.pair.token1.id,
    amount: token1Amount,
  },
  amountUSD,
})

const processTokenTransactions = (txs?: TokenTransactionsResponse): Transaction[] => {
  if (!txs) return []

  const mints = (txs.mintV2S ?? []).map((tx) =>
    toProcessedTransaction(
      tx,
      TransactionType.MINT,
      tx.to ?? '',
      parseAmount(tx.amount0),
      parseAmount(tx.amount1),
      parseAmount(tx.amountUSD)
    )
  )

  const burns = (txs.burnV2S ?? []).map((tx) =>
    toProcessedTransaction(
      tx,
      TransactionType.BURN,
      tx.sender ?? '',
      parseAmount(tx.amount0),
      parseAmount(tx.amount1),
      parseAmount(tx.amountUSD)
    )
  )

  const swaps = (txs.swapV2S ?? []).map((tx) =>
    toProcessedTransaction(
      tx,
      TransactionType.SWAP,
      tx.to ?? '',
      parseAmount(tx.amount0In) - parseAmount(tx.amount0Out),
      parseAmount(tx.amount1In) - parseAmount(tx.amount1Out),
      parseAmount(tx.amountUSD)
    )
  )

  return [...mints, ...burns, ...swaps].sort((a, b) => Number(b.timestamp) - Number(a.timestamp))
}

export const useV2TokenTransactions = (
  tokenAddress: string,
  options?: {
    enabled?: boolean
  }
) => {
  const subgraphConfig = useSubgraphConfig(SubgraphType.V2)
  const endpoint = subgraphConfig.endpoint
  const normalizedAddress = tokenAddress.toLowerCase()
  const enabled = (options?.enabled ?? true) && Boolean(tokenAddress)

  const tokenPairsQuery = useGetV2TokenPairsQuery(
    {
      endpoint,
    },
    {
      address: normalizedAddress,
    },
    {
      enabled,
      staleTime: Infinity,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      refetchInterval: false,
    }
  )

  const pairIds = useMemo(() => {
    if (!tokenPairsQuery.data) return undefined

    return [...(tokenPairsQuery.data.pairs0 ?? []), ...(tokenPairsQuery.data.pairs1 ?? [])]
      .map((pair) => pair?.id?.toLowerCase())
      .filter((id): id is string => Boolean(id))
  }, [tokenPairsQuery.data])

  const shouldFetchTransactions = enabled && Boolean(pairIds?.length)

  const {
    data = [],
    isLoading,
    error,
  } = useGetV2TokenTransactionsQuery(
    {
      endpoint,
    },
    shouldFetchTransactions
      ? {
          pairIds,
        }
      : undefined,
    {
      enabled: shouldFetchTransactions,
      staleTime: Infinity,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      refetchInterval: false,
      select: (data) => processTokenTransactions(data),
    }
  )

  return {
    data: shouldFetchTransactions ? data : [],
    isLoading: tokenPairsQuery.isLoading || (shouldFetchTransactions ? isLoading : false),
    error: error ?? tokenPairsQuery.error,
  }
}
